import { useMemo } from 'react';
import { IconTruck, IconUser } from '@tabler/icons-react';
import styled from 'styled-components';

import { GetDeliveriesBySiteIdQuery, GetDeliveryQuery, useGetDeliveriesBySiteIdQuery } from 'data/__generated__';

import { Chronology, ChronologyEntry } from 'components/DetailsView/Chronology';
import { SectionContainer } from 'components/DetailsView/Section';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { Loader } from 'components/Loader';
import { TotemLabel } from 'components/TotemLabel';

import { StatusTag } from '../components/StatusTag';
import { DELIVERY_STATES_LABELS, getColorsFromState } from '../constants/states';

type Delivery = NonNullable<GetDeliveryQuery['delivery']>;
type SiteDelivery = GetDeliveriesBySiteIdQuery['getDeliveriesBySiteId'][number];

/**
 * Ce qui est arrivé à la livraison, et ce qui arrive à ce site.
 *
 * Les deux historiques tenaient dans deux sections distantes — les statuts en haut, collés aux
 * boutons de transition, la chronologie du site tout en bas. Côte à côte, la hauteur de la page
 * devient le maximum des deux au lieu de leur somme.
 */
export const DeliveryHistorySection = ({ delivery }: { delivery: Delivery }) => (
    <SectionContainer title="Historique" isInitiallyOpen>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-8">
            {delivery.siteId ? (
                <div className="flex flex-col gap-[5px]">
                    <TotemLabel>Chronologie des livraisons du site</TotemLabel>
                    <SiteDeliveriesChronology
                        deliveryId={delivery._id}
                        siteId={delivery.siteId}
                        deliveryDate={delivery.deliveryDate}
                    />
                </div>
            ) : null}

            <StateHistory
                label="Statuts de cette livraison"
                records={delivery.stateHistory.map((stateRecord) => ({
                    ...stateRecord,
                    state: <StatusTag state={stateRecord.state} />,
                }))}
            />
        </div>
    </SectionContainer>
);

/**
 * Chargée par sa propre query (`GET_DELIVERIES_BY_SITE_ID`) : la page reste affichable pendant le
 * chargement, et la sélection maigre ne rejoue pas le coût de `GET_DELIVERY`.
 */
const SiteDeliveriesChronology = ({
    deliveryId,
    siteId,
    deliveryDate,
}: {
    deliveryId: string;
    siteId: string;
    /** `BirthdayDate` : le pivot de la chronologie se compare tel quel, côté serveur. */
    deliveryDate: string;
}) => {
    const { data, loading, error } = useGetDeliveriesBySiteIdQuery({
        variables: { siteId, deliveryDate },
    });

    const deliveries = data?.getDeliveriesBySiteId ?? [];

    const entries = useMemo<ChronologyEntry[]>(() => deliveries.map(toChronologyEntry), [deliveries]);

    if (loading) {
        return <Loader />;
    }

    if (error || !data) {
        return <Message $isError>Erreur lors du chargement des autres livraisons</Message>;
    }

    if (!deliveries.some(({ _id }) => _id !== deliveryId)) {
        return <Message>Aucune autre livraison trouvée pour ce site</Message>;
    }

    return <Chronology entries={entries} currentEntryId={deliveryId} />;
};

function toChronologyEntry({ _id, deliveryDate, deliveryPerson, roundName, state }: SiteDelivery): ChronologyEntry {
    const { backgroundColor, labelColor } = getColorsFromState(state);

    return {
        id: _id,
        date: deliveryDate,
        color: backgroundColor,
        labelColor,
        stateLabel: DELIVERY_STATES_LABELS[state],
        href: `/delivery/${_id}`,
        meta: (
            <MetaList>
                {roundName ? (
                    <MetaItem>
                        <IconTruck size={14} />
                        {roundName}
                    </MetaItem>
                ) : null}
                {deliveryPerson?.profile.fullname ? (
                    <MetaItem>
                        <IconUser size={14} />
                        {deliveryPerson.profile.fullname}
                    </MetaItem>
                ) : null}
            </MetaList>
        ),
    };
}

const MetaList = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MetaItem = styled.span`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const Message = styled.div<{ $isError?: boolean }>`
    padding: 20px 0;
    text-align: center;
    font-style: ${({ $isError }) => ($isError ? 'normal' : 'italic')};
    color: ${({ $isError, theme }) => ($isError ? theme.errorColor : theme.textColor)};
`;
